import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, RefreshCw, ShieldCheck } from 'lucide-react';

import { Button } from '../../ui/button';
import { AdminActiveStatusBadge } from './AdminActiveStatusBadge';
import {
    ADMIN_BADGE_SOLID_GREEN_CLASS,
    ADMIN_BADGE_SOLID_RED_CLASS,
} from './adminSolidStatusBadge';
import { AccessBadge } from './CompanyDetailPrimitives';
import type { CompanySsoDetailState } from './companySsoDetail';

export function CompanyDetailHeader({
    companyId,
    companyDisplayName,
    companyIsActive,
    companyHasProAccess,
    companyHasPulseAccess,
    onBack,
    onEditCompany,
    isEditDisabled,
    ssoDetailState,
    teamsLoaded,
    isCompanySyncedToAuth0,
    isCompanyAuth0Syncing,
    canSyncCompanyUsers,
    onSyncCompany,
    onOpenHistoricalImport,
}: {
    companyId?: string;
    companyDisplayName: string;
    companyIsActive?: boolean;
    companyHasProAccess: boolean;
    companyHasPulseAccess: boolean;
    onBack: () => void;
    onEditCompany: () => void;
    isEditDisabled: boolean;
    ssoDetailState: CompanySsoDetailState;
    teamsLoaded: boolean;
    isCompanySyncedToAuth0: boolean;
    isCompanyAuth0Syncing: boolean;
    canSyncCompanyUsers: boolean;
    onSyncCompany: (syncUsers: boolean) => void;
    onOpenHistoricalImport: () => void;
}) {
    return (
        <div className="relative z-20 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3">
                <Button variant="ghost" size="icon" onClick={onBack}>
                    <ArrowLeft className="size-5" />
                </Button>
                <div>
                    <p className="text-sm text-neutral-500">Company</p>
                    <h1 className="text-3xl font-semibold text-neutral-950">
                        {companyDisplayName}
                    </h1>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                        <p className="text-sm text-neutral-500">ID: {companyId}</p>
                        {companyIsActive !== undefined && (
                            <AdminActiveStatusBadge isActive={companyIsActive} />
                        )}
                        <AccessBadge label="Pro" hasAccess={companyHasProAccess} />
                        <AccessBadge label="Pulse" hasAccess={companyHasPulseAccess} />
                    </div>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onEditCompany}
                    disabled={isEditDisabled}
                >
                    Edit
                </Button>
                <details className="relative z-30 shrink-0">
                    <summary className="flex cursor-pointer list-none items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-neutral-900 transition-colors hover:bg-neutral-50">
                        Developer
                        <ChevronDown className="size-4 text-neutral-500" aria-hidden />
                    </summary>
                    <div className="absolute right-0 z-40 mt-2 flex min-w-56 flex-col rounded-lg border border-neutral-200 bg-white p-2 shadow-lg">
                        <button
                            type="button"
                            className="rounded-md px-3 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                            onClick={onOpenHistoricalImport}
                        >
                            Historical import
                        </button>
                        {ssoDetailState.manageSsoDisabled || !ssoDetailState.manageSsoPath ? (
                            <button
                                type="button"
                                disabled
                                title={ssoDetailState.manageSsoTooltip ?? undefined}
                                className="flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-neutral-400"
                            >
                                <ShieldCheck className="size-4" aria-hidden />
                                Manage SSO
                            </button>
                        ) : (
                            <Link
                                to={ssoDetailState.manageSsoPath}
                                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                            >
                                <ShieldCheck className="size-4" aria-hidden />
                                Manage SSO
                            </Link>
                        )}
                        {!isCompanySyncedToAuth0 ? (
                            <>
                                <button
                                    type="button"
                                    aria-label="Sync company organization to Auth0"
                                    disabled={isCompanyAuth0Syncing}
                                    className="flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50 disabled:text-neutral-400"
                                    onClick={() => onSyncCompany(false)}
                                >
                                    <RefreshCw
                                        className={`size-4 ${isCompanyAuth0Syncing ? 'animate-spin' : ''}`}
                                        aria-hidden
                                    />
                                    Org sync
                                </button>
                                <button
                                    type="button"
                                    aria-label="Sync company organization and users to Auth0"
                                    disabled={isCompanyAuth0Syncing || !canSyncCompanyUsers}
                                    title={
                                        canSyncCompanyUsers
                                            ? undefined
                                            : 'Requires administrate:users permission'
                                    }
                                    className="flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50 disabled:text-neutral-400"
                                    onClick={() => onSyncCompany(true)}
                                >
                                    <RefreshCw
                                        className={`size-4 ${isCompanyAuth0Syncing ? 'animate-spin' : ''}`}
                                        aria-hidden
                                    />
                                    Org + users sync
                                </button>
                            </>
                        ) : null}
                    </div>
                </details>
                {teamsLoaded &&
                    (isCompanySyncedToAuth0 ? (
                        <>
                            <span
                                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${ADMIN_BADGE_SOLID_GREEN_CLASS}`}
                            >
                                Auth0 synced
                            </span>
                            <span
                                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${ssoDetailState.badgeClassName}`}
                            >
                                {ssoDetailState.badgeLabel}
                            </span>
                        </>
                    ) : (
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span
                                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${ADMIN_BADGE_SOLID_RED_CLASS}`}
                            >
                                Auth0 not synced
                            </span>
                        </div>
                    ))}
            </div>
        </div>
    );
}
